import React from "react";
import { useDocumentLog } from "@/hooks/useDocumentLog";
import { getMemberInfo } from "@/hooks/useMembers";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import MHeader from "@/components/ui/organisms/MHeader";
import MInfoCard from "@/components/ui/atoms/MInfoCard";
import MLoader from "@/components/ui/atoms/MLoader";
import MFilter from "@/components/ui/atoms/MFilter";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import MChip from "@/components/ui/atoms/MChip";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import {
  DocumentLogSource,
  DocumentLogStatus,
  getDocumentLogSourceLabel,
  DOCUMENT_LOG_STATUS_LABELS,
  DOCUMENT_LOG_STATUS_COLORS,
} from "@/types/DocumentLog";
import { formatCommentDate } from "@/utils/date-utils";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MDialog from "@/components/ui/atoms/MDialog";
import { IconEdit, IconTrash, IconReload } from "@tabler/icons-react";
import MInputField from "@/components/ui/atoms/MInputField";
import {
  DocumentStatus,
  FileCategory,
  fileCategoryLabels,
} from "@/types/document.dto";
import styles from "./document.module.css";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import { PAGE_SIZE } from "@/utils/constants";
import UncategorizedDrawer from "./UncategorizedDrawer";
import { useDocument } from "@/hooks/useDocument";
import DocumentDrawer from "./DocumentDrawer";
import DocumentCommentDrawer from "./DocumentCommentDrawer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScrollRestore } from "@/hooks/useScrollRestore";

const DocumentLogListing: React.FC = () => {
  const { t } = useTranslation();
  const member = getMemberInfo();
  const navigate = useNavigate();
  const loggedInMember = useSelector((state: RootState) => state.auth?.member);
  const loggedInMemberId = loggedInMember?.id || "";
  const { fetchDocumentLogs } = useDocumentLog();
  const {
    updateUncategorizedDocument,
    updateDocumentFlag,
    deleteDocument,
    updateDocumentNotes,
  } = useDocument(member.id);

  const [logs, setLogs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [hasMore, setHasMore] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const pageRef = React.useRef(1);

  const { saveScroll, saveItemCount, getSavedItemCount } = useScrollRestore(
    "documentLogListing",
    loading
  );

  const [selectedLog, setSelectedLog] = React.useState<any | null>(null);
  const [selectedDocument, setSelectedDocument] = React.useState<any | null>(
    null
  );
  const [uncategorizedDrawerOpen, setUncategorizedDrawerOpen] =
    React.useState(false);
  const [documentDrawerOpen, setDocumentDrawerOpen] = React.useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = React.useState(false);
  const [deletedInfoDrawerOpen, setDeletedInfoDrawerOpen] =
    React.useState(false);
  const [duplicateInfoDrawerOpen, setDuplicateInfoDrawerOpen] =
    React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const FILTER_STORAGE_KEY = "documentLogFilters";

  // Initialize filters from localStorage
  const getInitialFilters = () => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          years: parsed.years || [],
          sources: parsed.sources || [],
          statuses: parsed.statuses || [],
        };
      }
    } catch (err) {
      console.error("Failed to parse saved filters:", err);
    }
    return { years: [], sources: [], statuses: [] };
  };

  const initialFilters = getInitialFilters();
  const [selectedYears, setSelectedYears] = React.useState<string[]>(
    initialFilters.years
  );
  const [selectedSources, setSelectedSources] = React.useState<string[]>(
    initialFilters.sources
  );
  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>(
    initialFilters.statuses
  );
  const [yearDrawerOpen, setYearDrawerOpen] = React.useState(false);
  const [sourceDrawerOpen, setSourceDrawerOpen] = React.useState(false);
  const [statusDrawerOpen, setStatusDrawerOpen] = React.useState(false);
  const [allLogs, setAllLogs] = React.useState<any[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchInputValue, setSearchInputValue] = React.useState("");

  const buildFilters = React.useCallback(() => {
    const filters: any = {
      familyId: {
        operation: "EQUAL",
        value: member.current_family,
        type: "STRING",
      },
      uploadedBy: {
        operation: "EQUAL",
        value: loggedInMemberId,
        type: "STRING",
      },
    };
    if (selectedYears.length === 1) {
      const year = Number(selectedYears[0]);
      if (!isNaN(year)) {
        filters.updatedAt = {
          operation: "between",
          value: [
            `${year}-01-01T00:00:00.000Z`,
            `${year + 1}-01-01T00:00:00.000Z`,
          ],
          type: "DATE",
        };
      }
    } else if (selectedYears.length > 1) {
      filters.updatedAt = {
        operation: "or_between",
        value: selectedYears.map((y) => [
          `${y}-01-01T00:00:00.000Z`,
          `${Number(y) + 1}-01-01T00:00:00.000Z`,
        ]),
        type: "DATE",
      };
    }
    if (selectedSources.length === 1)
      filters.source = {
        operation: "EQUAL",
        value: selectedSources[0],
        type: "STRING",
      };
    else if (selectedSources.length > 1)
      filters.source = {
        operation: "in",
        value: selectedSources,
        type: "STRING",
      };
    if (selectedStatuses.length === 1)
      filters.status = {
        operation: "EQUAL",
        value: selectedStatuses[0],
        type: "STRING",
      };
    else if (selectedStatuses.length > 1)
      filters.status = {
        operation: "in",
        value: selectedStatuses,
        type: "STRING",
      };
    return filters;
  }, [
    member.current_family,
    loggedInMemberId,
    selectedYears,
    selectedSources,
    selectedStatuses,
  ]);

  const pollingRef = React.useRef<NodeJS.Timeout | null>(null);

  const hasProcessingLogs = React.useCallback(
    (logsArr: any[]) =>
      logsArr.some((log) => log.status === DocumentLogStatus.Processing),
    []
  );

  React.useEffect(() => {
    const filtersToSave = {
      years: selectedYears,
      sources: selectedSources,
      statuses: selectedStatuses,
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [selectedYears, selectedSources, selectedStatuses]);

  const loadLogs = React.useCallback(
    async (reset = false) => {
      try {
        if (reset) {
          setLoading(true);
          setPage(1);
          pageRef.current = 1;
          setHasMore(true);

          const filters = buildFilters();
          const savedCount = getSavedItemCount();
          let allData: any[] = [];
          let currentPage = 1;

          while (true) {
            const payload = {
              filters,
              sort: [{ field: "updatedAt", order: "desc" }],
              pagination: { page: currentPage, pageSize: PAGE_SIZE },
              page: currentPage,
              pageSize: PAGE_SIZE,
            };

            const res = await fetchDocumentLogs(payload);
            const data = Array.isArray(res?.data) ? res.data : [];
            allData = [...allData, ...data];

            if (data.length < PAGE_SIZE) {
              setHasMore(false);
              break;
            }

            if (savedCount && allData.length >= savedCount) {
              setHasMore(true);
              break;
            }

            if (!savedCount) {
              break;
            }

            currentPage++;
          }

          setLogs(allData);
          setAllLogs(allData);
          pageRef.current = currentPage + 1;
          setPage(currentPage + 1);
        } else {
          const currentPage = pageRef.current;
          const filters = buildFilters();

          const payload = {
            filters,
            sort: [{ field: "updatedAt", order: "desc" }],
            pagination: { page: currentPage, pageSize: PAGE_SIZE },
            page: currentPage,
            pageSize: PAGE_SIZE,
          };

          const res = await fetchDocumentLogs(payload);
          const data = Array.isArray(res?.data) ? res.data : [];

          setLogs((prev) => [...prev, ...data]);
          setAllLogs((prev) => {
            const seen = new Set(prev.map((item) => item._id));
            const merged = [...prev];
            for (const d of data) {
              if (!seen.has(d._id)) merged.push(d);
            }
            return merged;
          });

          setHasMore(data.length === PAGE_SIZE);

          if (data.length > 0) {
            setPage((prev) => {
              const next = prev + 1;
              pageRef.current = next;
              return next;
            });
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [buildFilters, fetchDocumentLogs, getSavedItemCount]
  );

  // Polling logic: poll if any logs are processing
  React.useEffect(() => {
    if (hasProcessingLogs(logs)) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => loadLogs(true), 2000);
      }
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [logs, hasProcessingLogs, loadLogs]);

  React.useEffect(() => {
    loadLogs(true);
  }, [selectedYears, selectedSources, selectedStatuses]);

  return (
    <>
      <div style={{ paddingLeft: 24, paddingRight: 24 }}>
        <MHeader
          module="documents"
          memberInfo={member}
          subHeading={t("documentLogListing.title")}
          showGradient
          description={t("documentLogListing.description")}
        />

        <div style={{ marginBottom: 16 }}>
          <div className={styles.documentFilterRow} style={{ marginTop: 24 }}>
            <MFilter
              text={"Year"}
              selected={selectedYears.length > 0}
              onClick={() => setYearDrawerOpen(true)}
              variant="sm"
            />
            <MDropDownDrawer
              open={yearDrawerOpen}
              onClose={() => setYearDrawerOpen(false)}
              title="Select Year"
              items={(() => {
                const presentYears = Array.from(
                  new Set(
                    (allLogs || [])
                      .map((d) => {
                        const date = d.updatedAt ? new Date(d.updatedAt) : null;
                        return date ? date.getFullYear() : undefined;
                      })
                      .filter((y) => !!y)
                  )
                );
                return presentYears.map((y) => ({
                  value: String(y),
                  label: String(y),
                }));
              })()}
              variant="multi"
              selectedValues={selectedYears}
              onSelect={(vals) => {
                setSelectedYears(Array.isArray(vals) ? vals : [vals]);
                setYearDrawerOpen(false);
              }}
              height="50vh"
            />
            <MFilter
              text={"Source"}
              selected={selectedSources.length > 0}
              onClick={() => setSourceDrawerOpen(true)}
              variant="sm"
            />
            <MDropDownDrawer
              open={sourceDrawerOpen}
              onClose={() => setSourceDrawerOpen(false)}
              title="Select Source"
              items={Object.values(DocumentLogSource).map((val) => ({
                value: val,
                label: getDocumentLogSourceLabel(val as DocumentLogSource, t),
              }))}
              variant="multi"
              selectedValues={selectedSources}
              onSelect={(vals) => {
                setSelectedSources(Array.isArray(vals) ? vals : [vals]);
                setSourceDrawerOpen(false);
              }}
            />
            <MFilter
              text={"Status"}
              selected={selectedStatuses.length > 0}
              onClick={() => setStatusDrawerOpen(true)}
              variant="sm"
            />
            <MDropDownDrawer
              open={statusDrawerOpen}
              onClose={() => setStatusDrawerOpen(false)}
              title="Select Status"
              items={Object.values(DocumentLogStatus).map((val) => ({
                value: val,
                label: DOCUMENT_LOG_STATUS_LABELS[val as DocumentLogStatus],
              }))}
              variant="multi"
              selectedValues={selectedStatuses}
              onSelect={(vals) => {
                setSelectedStatuses(Array.isArray(vals) ? vals : [vals]);
                setStatusDrawerOpen(false);
              }}
            />
            <button
              type="button"
              className={styles.resetButton}
              disabled={
                selectedYears.length === 0 &&
                selectedSources.length === 0 &&
                selectedStatuses.length === 0
              }
              onClick={() => {
                setSelectedYears([]);
                setSelectedSources([]);
                setSelectedStatuses([]);
              }}
            >
              <IconReload size={12} className={styles.resetIcon} />
              <span className={styles.resetText}>{t("common.reset")}</span>
            </button>
          </div>

          {(selectedSources.length > 0 ||
            selectedYears.length > 0 ||
            selectedStatuses.length > 0) && (
            <div
              style={{
                display: "flex",
                gap: 12,
                margin: "16px 0 0 0",
                flexWrap: "wrap",
              }}
            >
              {selectedSources.map((source) => (
                <MChip
                  key={`source-${source}`}
                  text={getDocumentLogSourceLabel(
                    source as DocumentLogSource,
                    t
                  )}
                  variant="filtered"
                  selected
                  onRightIconClick={() =>
                    setSelectedSources(
                      selectedSources.filter((s) => s !== source)
                    )
                  }
                />
              ))}
              {selectedYears.map((year) => (
                <MChip
                  key={`year-${year}`}
                  text={year}
                  variant="filtered"
                  selected
                  onRightIconClick={() =>
                    setSelectedYears(selectedYears.filter((y) => y !== year))
                  }
                />
              ))}
              {selectedStatuses.map((status) => (
                <MChip
                  key={`status-${status}`}
                  text={DOCUMENT_LOG_STATUS_LABELS[status as DocumentLogStatus]}
                  variant="filtered"
                  selected
                  onRightIconClick={() =>
                    setSelectedStatuses(
                      selectedStatuses.filter((s) => s !== status)
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>

        {loading && logs.length === 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <MLoader size={56} color="#4C189A" />
          </div>
        ) : logs.length === 0 ? (
          <MEmptyCard
            primaryTitle="No uploads found"
            secondaryTitle="Try another filter or upload new documents"
          />
        ) : (
          <MInfiniteScroll
            data={logs}
            hasMore={hasMore}
            isLoading={loading}
            onLoadMore={() => loadLogs(false)}
            renderItem={(log, idx) => {
              const isProcessed = log.status === DocumentLogStatus.Assigned;
              const isReview = log.status === DocumentLogStatus.Uncategorized;
              const isProcessing = log.status === DocumentLogStatus.Processing;
              const isDeleted = log.status === DocumentLogStatus.Deleted;
              const isDuplicate = log.status === DocumentLogStatus.Duplicate;

              const sourceLabel = log.source
                ? getDocumentLogSourceLabel(log.source as DocumentLogSource, t)
                : t("common.documentLogSource.files");
              const dateLabel = log.createdAt
                ? formatCommentDate(log.createdAt)
                : "";
              const metaLine = dateLabel
                ? `${sourceLabel} · ${dateLabel}`
                : sourceLabel;

              const assignedName = log.member
                ? `${log.member.nickname || log.member.firstName}`.trim()
                : "";

              const docType =
                fileCategoryLabels[log.category as FileCategory] || "";

              const statusLabel = isProcessed
                ? "Processed"
                : isReview
                ? "Review"
                : isProcessing
                ? "Processing"
                : isDeleted
                ? "Deleted"
                : isDuplicate
                ? "Duplicate"
                : String(log.status || "");

              const statusColor: any = isProcessed
                ? "green"
                : isReview
                ? "yellow"
                : isProcessing
                ? "orange"
                : isDeleted
                ? "grey"
                : isDuplicate
                ? "duplicate"
                : log.status
                ? DOCUMENT_LOG_STATUS_COLORS[log.status as DocumentLogStatus]
                : "orange";

              const handleDotsClick = () => {
                setSelectedLog(log);
                setSelectedDocument(log.document || null);
                if (isProcessed) {
                  setDocumentDrawerOpen(true);
                  return;
                }
                // For review cards, options menu will be shown via actions prop
              };

              const handleNameClick = () => {
                if (isDeleted || isProcessing || isDuplicate) return;
                if (!isProcessed && !isReview) return;
                if (!log.fileKey) return;
                if (!log.document) return;
                saveScroll();
                saveItemCount(logs.length);
                navigate("/documents/view", {
                  state: {
                    document: log.document,
                    backUrl: "documents/log-listing",
                  },
                });
              };

              const handleCardClick = () => {
                if (isDeleted) {
                  setSelectedLog(log);
                  setDeletedInfoDrawerOpen(true);
                }
              };

              // Review cards show Edit and Delete options menu
              const reviewActions = isReview
                ? [
                    {
                      id: "edit",
                      label: "Review",
                      icon: <IconEdit size={20} />,
                      onClick: () => {
                        setSelectedLog(log);
                        setSelectedDocument(log.document || null);
                        setUncategorizedDrawerOpen(true);
                      },
                    },
                    {
                      id: "delete",
                      label: "Delete",
                      icon: <IconTrash size={20} />,
                      onClick: () => {
                        setSelectedLog(log);
                        setSelectedDocument(log.document || null);
                        setDeleteDialogOpen(true);
                      },
                      delete: true,
                    },
                  ]
                : undefined;

              return (
                <MInfoCard
                  key={log._id || idx}
                  primaryLabel={log.displayName || log.originalFileName}
                  secondaryLabel={metaLine}
                  highlightedLabel={
                    isProcessed && assignedName && docType
                      ? `Assigned to ${assignedName} · ${docType}`
                      : undefined
                  }
                  warningLabel={
                    isReview
                      ? log.ocrError ||
                        "Could not identify document owner & type"
                      : isDuplicate
                      ? "Duplicate document not uploaded"
                      : undefined
                  }
                  onPrimaryClick={
                    (isProcessed || isReview) && log.fileKey && log.document
                      ? handleNameClick
                      : undefined
                  }
                  primaryClickDisabled={isProcessing}
                  wrapLabel
                  variant={isReview ? "actionsMenu" : "labels"}
                  labelText={statusLabel}
                  labelColor={statusColor}
                  actions={reviewActions}
                  showOptionsMenu={isProcessed || isDuplicate}
                  onOptionsClick={
                    isProcessed
                      ? handleDotsClick
                      : isDuplicate
                      ? () => {
                          setSelectedLog(log);
                          setDuplicateInfoDrawerOpen(true);
                        }
                      : undefined
                  }
                  onClick={isDeleted ? handleCardClick : undefined}
                />
              );
            }}
          />
        )}
      </div>

      <DocumentDrawer
        open={documentDrawerOpen}
        onClose={() => setDocumentDrawerOpen(false)}
        displayName={
          selectedDocument?.displayName ||
          selectedLog?.displayName ||
          selectedLog?.originalFileName ||
          ""
        }
        fileKey={selectedDocument?.media?.fileKey || selectedLog?.fileKey}
        fileName={
          selectedDocument?.media?.fileName || selectedLog?.originalFileName
        }
        isImportant={!!selectedDocument?.isImportant}
        isPrivate={!!selectedDocument?.isPrivate}
        availableOffline={!!selectedDocument?.availableOffline}
        category={selectedDocument?.category}
        document={selectedDocument}
        onUpdate={() => loadLogs(true)}
        onImportantChange={async (checked) => {
          if (!selectedDocument) return;
          await updateDocumentFlag(selectedDocument, "isImportant", checked);
          setSelectedDocument((doc) => ({ ...doc, isImportant: checked }));
        }}
        onPrivateChange={async (checked) => {
          if (!selectedDocument) return;
          await updateDocumentFlag(selectedDocument, "isPrivate", checked);
          setSelectedDocument((doc) => ({ ...doc, isPrivate: checked }));
        }}
        onOfflineChange={async (checked) => {
          if (!selectedDocument) return;
          await updateDocumentFlag(
            selectedDocument,
            "availableOffline",
            checked
          );
          setSelectedDocument((doc) => ({ ...doc, availableOffline: checked }));
        }}
        onDelete={async () => {
          if (!selectedDocument) return;
          await deleteDocument(selectedDocument);
          setDocumentDrawerOpen(false);
          await loadLogs(true);
        }}
        onCommentClick={() => {
          setDocumentDrawerOpen(false);
          setCommentDrawerOpen(true);
        }}
        commentButtonText={
          selectedDocument?.notes ? "Edit Comment" : "Add Comment"
        }
      />

      <DocumentCommentDrawer
        open={commentDrawerOpen}
        onClose={() => setCommentDrawerOpen(false)}
        initialValue={selectedDocument?.notes || ""}
        onDone={async (notes) => {
          if (!selectedDocument) return;
          await updateDocumentNotes(selectedDocument, notes);
          setSelectedDocument((doc) => ({ ...doc, notes }));
          setCommentDrawerOpen(false);
        }}
        title={selectedDocument?.notes ? "Edit Comment" : "Add Comment"}
      />

      <FooterDrawer
        open={deletedInfoDrawerOpen}
        onClose={() => setDeletedInfoDrawerOpen(false)}
        title="Deletion details"
      >
        {selectedLog ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              padding: 24,
            }}
          >
            <MInputField
              label="Deleted by"
              value={`${selectedLog.lastUpdatedBy?.firstName || ""} ${
                selectedLog.lastUpdatedBy?.lastName || ""
              }`.trim()}
              readOnly
              variant="Underlined"
              size="md"
            />
            <MInputField
              label="Date & time"
              value={
                selectedLog.updatedAt
                  ? formatCommentDate(selectedLog.updatedAt)
                  : ""
              }
              readOnly
              variant="Underlined"
              size="md"
            />
          </div>
        ) : null}
      </FooterDrawer>

      <FooterDrawer
        open={duplicateInfoDrawerOpen}
        onClose={() => setDuplicateInfoDrawerOpen(false)}
        title="Document already in Maatra"
      >
        {selectedLog ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              padding: 24,
            }}
          >
            <MInputField
              label="Document name"
              value={
                selectedLog.document?.displayName ||
                selectedLog.displayName ||
                selectedLog.originalFileName ||
                ""
              }
              readOnly
              variant="SummaryDisplay"
              size="md"
            />
            <MInputField
              label="Document owner"
              value={(() => {
                const m = selectedLog.document?.member || selectedLog.member;
                if (!m) return "";
                return (m.nickname || m.firstName || "").trim();
              })()}
              readOnly
              variant="SummaryDisplay"
              size="md"
            />
            <MInputField
              label="Type"
              value={
                fileCategoryLabels[
                  selectedLog.document?.category as FileCategory
                ] || ""
              }
              readOnly
              variant="SummaryDisplay"
              size="md"
            />
          </div>
        ) : null}
      </FooterDrawer>

      <UncategorizedDrawer
        open={uncategorizedDrawerOpen}
        onClose={() => setUncategorizedDrawerOpen(false)}
        document={selectedDocument || selectedLog}
        onDone={async ({ displayName, assignedTo, fileCategory, comment }) => {
          if (!selectedDocument) return;
          await updateUncategorizedDocument(selectedDocument, {
            memberId: assignedTo?.id,
            category: fileCategory,
            notes: comment,
            displayName,
            categorize: true,
            status: DocumentStatus.Processed,
          });
          setUncategorizedDrawerOpen(false);
          await loadLogs(true);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <MDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete document?"
        description="This document will be permanently deleted."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={async () => {
          if (selectedDocument) {
            await deleteDocument(selectedDocument);
            setDeleteDialogOpen(false);
            await loadLogs(true);
          }
        }}
        onSecondaryCta={() => setDeleteDialogOpen(false)}
      />
    </>
  );
};

export default DocumentLogListing;
